import { ApolloClient } from '@apollo/client';
import { GetCandidateProfileExternalIdDocument } from 'generated-graphql/graphql';

type FetchCandidateProfileExternalIdsProps = {
  apolloClient: ApolloClient<object>;
  candidateProfileIds: string[];
};

/**
 * @description The customer's own ids for the given candidates. Unlike the link back to
 * their system, these do not ride along with the search results — the vector collection has
 * no such property — so they are read from the profiles when an action needs them.
 *
 * Read one candidate at a time by primary key rather than through the search index: an
 * identity lookup wants the record, not a query over analysed copies of it. The selection
 * these serve is capped at 100, so the reads go out together and are not chunked.
 * @returns the ids that resolved, in no particular order and with candidates that have none
 * left out
 * @throws when any lookup failed. A candidate with no external id cannot be sent and is a
 * fact about the data; a lookup that failed says nothing about the candidate, so carrying on
 * would hand the job system an incomplete selection and tell the recruiter the missing ones
 * had no external id. Failing lets them retry with one click instead.
 */
export const fetchCandidateProfileExternalIds = async ({
  apolloClient,
  candidateProfileIds
}: FetchCandidateProfileExternalIdsProps): Promise<string[]> => {
  if (candidateProfileIds.length === 0) {
    return [];
  }

  const results = await Promise.allSettled(
    candidateProfileIds.map((id) =>
      apolloClient.query({
        query: GetCandidateProfileExternalIdDocument,
        variables: { id }
      })
    )
  );

  const failed = results.filter((result) => result.status === 'rejected');

  if (failed.length > 0) {
    throw new Error(
      `${failed.length} of ${results.length} candidate profile lookups failed`
    );
  }

  return results
    .map((result) =>
      result.status === 'fulfilled'
        ? result.value.data?.getCandidateProfile?.externalId
        : null
    )
    .filter((externalId): externalId is string => Boolean(externalId));
};
